import React, { useState, useCallback } from 'react';
import { Pin, PinOff, X, RefreshCw, ChevronDown, ChevronUp, RotateCw } from 'lucide-react';
import { HealthResponse } from './ActionBarHealth';

type TabType = 'staging' | 'production';

/** Which health row maps to which systemd user unit. */
type ServiceKind = 'backend' | 'frontend' | 'tradovate' | 'cme' | 'accountStats' | 'news';

/**
 * The unit behind a row.
 *
 * Engine and frontend are per-environment; the other four are shared services
 * that run once and serve both, which is why the tab does not change them.
 * Restarting "News Service" from the staging tab restarts the same process the
 * production tab is showing — that is the deployment, not a bug here.
 */
function unitFor(kind: ServiceKind, tab: TabType): string {
  switch (kind) {
    case 'backend':
      return tab === 'staging' ? 'utt-engine-staging' : 'utt-engine';
    case 'frontend':
      return tab === 'staging' ? 'frontend-staging' : 'frontend-prod';
    case 'tradovate':
      return 'tradovate-service';
    case 'cme':
      return 'cme-market-hours';
    case 'accountStats':
      return 'account-stats';
    case 'news':
      return 'news-service';
  }
}

/**
 * Units worth a confirm, and what the operator is actually agreeing to.
 *
 * Not a generic "are you sure": the two that matter have specific, non-obvious
 * consequences, and a prompt that does not say what they are trains people to
 * click through it.
 */
function restartWarning(unit: string): string | null {
  if (unit === 'tradovate-service') {
    return 'Restarting tradovate-service DROPS your broker connections. Positions stay open at the broker, but the terminal loses its live view until it reconnects.\n\nRestart anyway?';
  }
  if (unit === 'utt-engine') {
    return 'This is the production engine. Restarting it interrupts live market data, signal generation and any auto-bot that is armed.\n\nRestart anyway?';
  }
  if (unit === 'frontend-prod') {
    return 'This serves the production UI. If you are looking at it now, the page will stop responding until it comes back.\n\nRestart anyway?';
  }
  return null;
}

interface ServiceHealth {
  name: string;
  port: number;
  status: 'online' | 'offline' | 'degraded';
  responseTime?: number;
  error?: string;
  metrics?: Record<string, unknown>;
}

interface HealthExpandedProps {
  healthData: HealthResponse | null;
  isLoading: boolean;
  error: string | null;
  isPinned: boolean;
  onTogglePin: () => void;
  onClose: () => void;
  onRefresh: () => void;
}

export const HealthExpanded: React.FC<HealthExpandedProps> = ({
  healthData,
  isLoading,
  error,
  isPinned,
  onTogglePin,
  onClose,
  onRefresh,
}) => {
  // Default to current environment
  const isStaging = window.location.port === '4002' ||
                    window.location.hostname.includes('staging');
  const [activeTab, setActiveTab] = useState<TabType>(isStaging ? 'staging' : 'production');
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const [restarting, setRestarting] = useState<string | null>(null);
  const [restartNote, setRestartNote] = useState<{ unit: string; ok: boolean; text: string } | null>(null);

  /**
   * Restart one unit.
   *
   * The awkward case is restarting the engine that is serving this page: it
   * kills the process mid-request, so `fetch` rejects and there is no response
   * to read. That is SUCCESS, not failure, and it is indistinguishable from a
   * genuine network error — so it is only treated as success for the unit we
   * know we are talking to. Any other unit failing to answer is a real error.
   */
  const handleRestart = useCallback(
    async (unit: string, label: string) => {
      const warning = restartWarning(unit);
      if (warning && !window.confirm(`${label}\n\n${warning}`)) return;

      const servingThisPage = isStaging ? 'utt-engine-staging' : 'utt-engine';
      setRestarting(unit);
      setRestartNote(null);
      try {
        const baseUrl = `${window.location.protocol}//${window.location.hostname}:${isStaging ? 9647 : 9547}`;
        const res = await fetch(`${baseUrl}/api/services/restart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unit }),
        });
        const data = await res.json();
        setRestartNote(
          data.success
            ? { unit, ok: true, text: `${label} restarted` }
            : { unit, ok: false, text: data.error || 'restart failed' }
        );
      } catch {
        setRestartNote(
          unit === servingThisPage
            ? { unit, ok: true, text: `${label} restarting — it dropped this connection, which is expected` }
            : { unit, ok: false, text: 'could not reach the engine' }
        );
      } finally {
        setRestarting(null);
        // Let it come back up before re-reading health, or every row reports
        // offline for a few seconds and looks like the restart broke something.
        setTimeout(onRefresh, 4000);
      }
    },
    [isStaging, onRefresh]
  );

  const toggleExpanded = useCallback((key: string) => {
    setExpandedServices(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const formatUptime = useCallback((ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  }, []);

  const formatBytes = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const renderServiceRow = (service: ServiceHealth, key: string, kind: ServiceKind) => {
    const isExpanded = expandedServices.has(key);
    const hasMetrics = service.metrics && Object.keys(service.metrics).length > 0;
    const unit = unitFor(kind, activeTab);
    const isRestarting = restarting === unit;
    const note = restartNote?.unit === unit ? restartNote : null;

    return (
      <div key={key} className={`abh-service-row ${service.status}`}>
        <div
          className="abh-service-header"
          onClick={() => hasMetrics && toggleExpanded(key)}
          style={{ cursor: hasMetrics ? 'pointer' : 'default' }}
        >
          <div className="abh-service-info">
            <span className={`abh-status-dot ${service.status}`} />
            <span className="abh-service-name">{service.name}</span>
            <span className="abh-service-port">:{service.port}</span>
          </div>
          <div className="abh-service-meta">
            {service.responseTime !== undefined && (
              <span className="abh-response-time">{service.responseTime}ms</span>
            )}
            {service.error && (
              <span className="abh-error-badge" title={service.error}>!</span>
            )}
            {/* stopPropagation: the row header toggles the metrics panel, and a
                restart that also expanded the row would be two actions on one
                click. */}
            <button
              className={`abh-restart-btn ${isRestarting ? 'busy' : ''}`}
              title={`Restart ${unit}`}
              disabled={isRestarting}
              onClick={(e) => {
                e.stopPropagation();
                handleRestart(unit, service.name);
              }}
            >
              <RotateCw size={12} />
            </button>
            {hasMetrics && (
              isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            )}
          </div>
        </div>

        {note && (
          <div className={`abh-restart-note ${note.ok ? 'ok' : 'err'}`}>{note.text}</div>
        )}

        {isExpanded && service.metrics && (
          <div className="abh-service-metrics">
            {Object.entries(service.metrics).map(([metricKey, value]) => {
              let displayValue = String(value);
              if (metricKey === 'memory' && typeof value === 'number') {
                displayValue = formatBytes(value);
              } else if (metricKey === 'uptime' && typeof value === 'number') {
                const uptimeMs = Date.now() - value;
                displayValue = formatUptime(uptimeMs);
              } else if (metricKey === 'cpu' && typeof value === 'number') {
                displayValue = `${value.toFixed(1)}%`;
              } else if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
              } else if (typeof value === 'object') {
                displayValue = JSON.stringify(value);
              }

              return (
                <div key={metricKey} className="abh-metric">
                  <span className="abh-metric-label">{metricKey}</span>
                  <span className="abh-metric-value">{displayValue}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const getStatusSummary = (env: HealthResponse['staging'] | undefined) => {
    if (!env) return { online: 0, total: 0 };
    const services = [
      env.backend,
      env.frontend,
      env.services.tradovate,
      env.services.cmeMarketHours,
      env.services.accountStats,
      env.services.news,
    ];
    return {
      online: services.filter(s => s.status === 'online').length,
      total: services.length,
    };
  };

  const currentEnv = healthData?.[activeTab];
  const stagingSummary = getStatusSummary(healthData?.staging);
  const productionSummary = getStatusSummary(healthData?.production);

  return (
    <div className="abh-expanded">
      {/* Header */}
      <div className="abh-expanded-header">
        <span className="abh-expanded-title">Service Health</span>
        <div className="abh-expanded-actions">
          <button
            className={`abh-action-btn ${isLoading ? 'loading' : ''}`}
            onClick={onRefresh}
            disabled={isLoading}
            title="Refresh"
          >
            <RefreshCw size={14} className={isLoading ? 'spinning' : ''} />
          </button>
          <button
            className="abh-action-btn"
            onClick={onTogglePin}
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
          </button>
          <button className="abh-action-btn" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="abh-tabs">
        <button
          className={`abh-tab ${activeTab === 'staging' ? 'active' : ''}`}
          onClick={() => setActiveTab('staging')}
        >
          Staging
          <span className={`abh-tab-badge ${stagingSummary.online === stagingSummary.total ? 'healthy' : 'degraded'}`}>
            {stagingSummary.online}/{stagingSummary.total}
          </span>
        </button>
        <button
          className={`abh-tab ${activeTab === 'production' ? 'active' : ''}`}
          onClick={() => setActiveTab('production')}
        >
          Prod
          <span className={`abh-tab-badge ${productionSummary.online === productionSummary.total ? 'healthy' : 'degraded'}`}>
            {productionSummary.online}/{productionSummary.total}
          </span>
        </button>
      </div>

      {/* Content */}
      <div className="abh-expanded-content">
        {isLoading && !healthData ? (
          <div className="abh-loading">
            <RefreshCw size={16} className="spinning" />
            <span>Checking...</span>
          </div>
        ) : error ? (
          <div className="abh-error">
            <span>Error: {error}</span>
            <button onClick={onRefresh}>Retry</button>
          </div>
        ) : currentEnv ? (
          <>
            <div className="abh-section">
              <div className="abh-section-title">Core</div>
              {renderServiceRow(currentEnv.backend, `${activeTab}-backend`, 'backend')}
              {renderServiceRow(currentEnv.frontend, `${activeTab}-frontend`, 'frontend')}
            </div>
            <div className="abh-section">
              <div className="abh-section-title">Shared</div>
              {renderServiceRow(currentEnv.services.tradovate, `${activeTab}-tradovate`, 'tradovate')}
              {renderServiceRow(currentEnv.services.cmeMarketHours, `${activeTab}-cme`, 'cme')}
              {renderServiceRow(currentEnv.services.accountStats, `${activeTab}-accountStats`, 'accountStats')}
              {renderServiceRow(currentEnv.services.news, `${activeTab}-news`, 'news')}
            </div>
          </>
        ) : (
          <div className="abh-empty">No data</div>
        )}
      </div>

      {/* Timestamp */}
      {healthData?.timestamp && (
        <div className="abh-timestamp">
          {new Date(healthData.timestamp).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};
